import { LoadingSpinner } from '@almbrand/loadingspinner';
import { RichText } from '@almbrand/richtext';
import { Tag } from '@almbrand/tag';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { ReactNode, useContext, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setModal } from 'store/slices/modalSlice';
import { meetDependency } from '../services/DependencyService';
import { replacePlaceholdersModal } from '../services/PlaceholderService';
import { useModal } from './useModal';

export const useCustomerModal = () => {
	const pageContext = useContext(PageContext);
	const { metaArea, theme } = pageContext ?? {};

	const customer = useAppSelector((state) => state.customer);
	const customerProducts = useAppSelector((state) => state.customerProducts);
	const form = useAppSelector((state) => state.calculatorForm);

	const dispatch = useAppDispatch();
	const modal = useModal();
	const themeName = ThemeNames[theme];

	useEffect(() => {
		if (!customer.loading && !customer.isLoggedIn) {
			dispatch(setModal({ displayModal: false }));
		}

		if (customer.loading) {
			dispatch(
				setModal({
					displayModal: true,
					modalProps: {
						mobileVariation: 'fullHeightFullWidth',
						title: 'Henter oplysninger',
						content: (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									gap: '16px',
									marginBottom: '16px',
								}}
							>
								<LoadingSpinner
									color='primary'
									style='large'
									type='dottedCircle'
								/>
								øjeblik...
							</div>
						),
					},
				})
			);
		}
	}, [customer.loading, customer.isLoggedIn]);

	useEffect(() => {
		if (customer.isDataFetched && customerProducts.isProductDataFetched && form['CUSTOMER_CONCEPT']) {
			const cmsModal = getActiveCustomerModal(metaArea);
			if (!cmsModal) {
				dispatch(
					setModal({
						displayModal: true,
					})
				);
				return;
			}

			const btn = (cmsModal?.fields?.items[0]?.content as any)?.ctaGroup?.items[0]?.content;
			const btnProps = {
				title: btn?.label ?? 'Ok',
				buttonType: btn?.buttonType ?? 'primary',
				onClick: (e) => {
					e.preventDefault();
					dispatch(setModal({ displayModal: false }));
				},
			};
			const productTags = getProductTags(customerProducts, themeName);

			const replacedContent = getReplaceTokens(cmsModal.title, cmsModal.subtitle, cmsModal.content);
			const content = (
				<>
					<RichText description={replacedContent.content} />
					<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
						{productTags}
					</div>
				</>
			);
			modal.displayModal({
				title: replacedContent.title,
				subtitle: replacedContent.subtitle,
				content,
				fields: [],
				actionButtonProps: btnProps,
				mobileVariation: 'fullHeightFullWidth',
			});
		}
	}, [customer.isDataFetched, customer.isLoggedIn, customerProducts.isProductDataFetched, form['CUSTOMER_CONCEPT']]);
};

const getActiveCustomerModal = (metaArea: MetaArea) => {
	for (const modal of metaArea?.items[0]?.content?.customerModals?.items || []) {
		if (meetDependency(modal.content?.contentDependency)) {
			return modal?.content?.action;
		}
	}
};

const getReplaceTokens = (title: string, subtitle: string, content: string) => {
	const replaceTokens = {
		title,
		subtitle,
		content,
		contentType: 'ModalContentBlock',
	};
	replacePlaceholdersModal(replaceTokens);
	return replaceTokens;
};

const getProductTags = (customerProducts, themeName): ReactNode[] => {
	const tags = [];
	let key = 'useCustomerTags';
	const width = 24;
	const height = 24;
	for (const product of customerProducts?.uniqueProducts || []) {
		switch (product) {
			case 'house':
				tags.push(createTag(key, 'Hus', 'house', themeName, width, height));
				break;
			case 'car':
				tags.push(createTag(key, 'Bil', 'car', themeName, width, height));
				break;
			case 'home':
				tags.push(createTag(key, 'Indbo', 'house', themeName, width, height));
				break;
			case 'holidayhouse':
				tags.push(createTag(key, 'Feriehus', 'house', themeName, width, height));
				break;
			case 'accident':
				tags.push(createTag(key, 'Ulykke', 'warning_bold', themeName, 16, 16));
				break;
		}
		key += 1;
	}
	return tags;
};

const createTag = (key, title, icon, themeName, width, height) => {
	return (
		<Tag
			key={key}
			title={title}
			iconProps={{ themeName, icon, width, height }}
			readOnly={true}
		/>
	);
};
